import _slicedToArray from "@babel/runtime/helpers/esm/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/esm/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/esm/defineProperty";
var _excluded = ["forceVisible"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { NotificationInfo } from '@repay/cactus-icons';
import { border, color, colorStyle, radius, shadow } from '@repay/cactus-theme';
import PropTypes from 'prop-types';
import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { margin } from 'styled-system';
import { getDataProps } from '../helpers/omit';
import { usePositioning } from '../helpers/positionPopover';
import usePopup from '../helpers/usePopup';
import Modal from '../Modal/Modal';

var resetBorderRadii = function resetBorderRadii(tooltip, skip) {
  var radiusKeys = ['borderTopLeftRadius', 'borderTopRightRadius', 'borderBottomRightRadius', 'borderBottomLeftRadius'];
  radiusKeys.forEach(function (key) {
    if (key !== skip) {
      tooltip.style[key] = '';
    }
  });
};

var OFFSET = 8;

var cactusPosition = function cactusPosition(tooltip, trigger) {
  if (!trigger) return;
  var tooltipRect = tooltip.getBoundingClientRect();
  var triggerRect = trigger.getBoundingClientRect();
  var styles = {
    left: triggerRect.left,
    top: triggerRect.top - tooltipRect.height
  };
  var collisions = {
    top: triggerRect.top - tooltipRect.height < 0,
    right: window.innerWidth < triggerRect.left + tooltipRect.width,
    bottom: window.innerHeight < triggerRect.bottom + tooltipRect.height,
    left: triggerRect.left - tooltipRect.width < 0
  };
  var directionRight = collisions.left && !collisions.right;
  var directionBottom = collisions.top && !collisions.bottom;
  var center = collisions.right && collisions.left;

  if (center) {
    styles.marginLeft = -tooltipRect.width / 2;
    resetBorderRadii(tooltip);
  } else if (directionRight && !directionBottom) {
    styles.borderBottomLeftRadius = 0;
    resetBorderRadii(tooltip, 'borderBottomLeftRadius');
  } else if (directionRight && directionBottom) {
    styles.borderTopLeftRadius = 0;
    resetBorderRadii(tooltip, 'borderTopLeftRadius');
  } else if (!directionRight && !directionBottom) {
    styles.borderBottomRightRadius = 0;
    resetBorderRadii(tooltip, 'borderBottomRightRadius');
  } else if (!directionRight && directionBottom) {
    styles.borderTopRightRadius = 0;
    resetBorderRadii(tooltip, 'borderTopRightRadius');
  }

  var tooltipStyles = _objectSpread(_objectSpread({}, styles), {}, {
    left: center ? '50%' : directionRight ? triggerRect.left + OFFSET : triggerRect.right - OFFSET - tooltipRect.width,
    top: directionBottom ? triggerRect.top + triggerRect.height : triggerRect.top - tooltipRect.height
  });

  var keys = Object.keys(tooltipStyles);
  keys.forEach(function (key) {
    var style = tooltipStyles[key];
    tooltip.style[key] = "".concat(style, "px");
  });
}; // TODO: Remove this wrapper when we're ready for v10 breaking changes


var wrapPosition = function wrapPosition(position) {
  return function (tooltip, trigger) {
    var styles = position(trigger === null || trigger === void 0 ? void 0 : trigger.getBoundingClientRect(), tooltip.getBoundingClientRect());

    if (styles) {
      var keys = Object.keys(styles);
      keys.forEach(function (key) {
        var style = styles[key];

        if (typeof style !== 'string' || !style.includes('px')) {
          style = "".concat(style, "px");
        }

        tooltip.style[key] = style;
      });
    }
  };
};

var getStyledInfoColor = function getStyledInfoColor(props) {
  if (props.disabled) {
    return "color: ".concat(color(props, 'mediumGray'), ";");
  } else if (props.forceVisible) {
    return "color: ".concat(color(props, 'callToAction'), ";");
  } else if (!props.color) {
    return "color: ".concat(color(props, 'darkestContrast'), ";");
  } // The color prop is handled by the `NotificationInfo` icon.

};

var StyledInfo = /*#__PURE__*/styled(function (_ref) {
  var forceVisible = _ref.forceVisible,
      props = _objectWithoutProperties(_ref, _excluded);

  return /*#__PURE__*/React.createElement(NotificationInfo, props);
}).withConfig({
  displayName: "StyledInfo"
})(["outline:none;", ";"], getStyledInfoColor);

var TooltipBase = function TooltipBase(props) {
  var className = props.className,
      colorProp = props.color,
      disabled = props.disabled,
      label = props.label,
      ariaLabel = props.ariaLabel,
      id = props.id,
      maxWidth = props.maxWidth,
      position = props.position,
      forceVisible = props.forceVisible;
  var triggerRef = useRef(null);
  var tooltipRef = useRef(null);

  var _useState = useState(false),
      _useState2 = _slicedToArray(_useState, 2),
      stayOpen = _useState2[0],
      setStayOpen = _useState2[1];

  var hoveringPopup = useRef(false);
  var positionFn = position ? wrapPosition(position) : cactusPosition;

  var _usePopup = usePopup('menu', {
    popupId: id,
    positionPopup: positionFn
  }),
      triggerProps = _usePopup.buttonProps,
      popupProps = _usePopup.popupProps,
      toggle = _usePopup.toggle,
      expanded = _usePopup.expanded;

  var visible = stayOpen || expanded || forceVisible || false;
  usePositioning({
    position: positionFn,
    visible: visible,
    ref: tooltipRef,
    anchorRef: triggerRef,
    updateOnScroll: true
  });
  useEffect(function () {
    var handleBodyClick = function handleBodyClick(event) {
      var _triggerRef$current, _tooltipRef$current;

      var target = event.target;

      if (!(target instanceof Node) || !((_triggerRef$current = triggerRef.current) !== null && _triggerRef$current !== void 0 && _triggerRef$current.contains(target)) && !((_tooltipRef$current = tooltipRef.current) !== null && _tooltipRef$current !== void 0 && _tooltipRef$current.contains(target))) {
        toggle(false);
        setStayOpen(false);
      }
    };

    document.body.addEventListener('click', handleBodyClick, true);
    return function () {
      document.body.removeEventListener('click', handleBodyClick, true);
    };
  }, [toggle]);

  var delayHovering = function delayHovering(value) {
    setTimeout(function () {
      if (!hoveringPopup.current) toggle(value);
    }, 500);
  };

  return /*#__PURE__*/React.createElement(React.Fragment, null, !disabled && /*#__PURE__*/React.createElement(TooltipPopup, Object.assign({
    id: popupProps.id,
    role: "tooltip",
    ref: tooltipRef,
    "aria-label": ariaLabel,
    $visible: visible,
    style: {
      maxWidth: maxWidth
    },
    onMouseEnter: function onMouseEnter() {
      hoveringPopup.current = true;
    },
    onMouseLeave: function onMouseLeave() {
      hoveringPopup.current = false;
      delayHovering(false);
    }
  }, getDataProps(props)), label), /*#__PURE__*/React.createElement("span", {
    id: triggerProps.id,
    ref: triggerRef,
    className: className,
    onClick: function onClick() {
      setStayOpen(true);
    },
    onMouseEnter: function onMouseEnter() {
      return delayHovering(true);
    },
    onMouseLeave: function onMouseLeave() {
      return delayHovering(false);
    }
  }, /*#__PURE__*/React.createElement(StyledInfo, {
    color: colorProp,
    disabled: disabled,
    forceVisible: visible
  })));
};

export var TooltipPopup = /*#__PURE__*/styled.div.withConfig({
  displayName: "TooltipPopup"
})(["z-index:100;position:fixed;padding:16px;", ";font-size:15px;", ";box-sizing:border-box;overflow-wrap:break-word;word-wrap:break-word;border:", ";border-radius:", ";display:block;", " ", " &{z-index:102;}"], shadow(1), colorStyle('standard'), border('callToAction'), radius(8), function (p) {
  return !p.$visible && "\n      transform: scale(0);\n      opacity: 0;\n    ";
}, Modal);
export var Tooltip = /*#__PURE__*/styled(TooltipBase).withConfig({
  displayName: "Tooltip"
})(["", ""], margin);
Tooltip.propTypes = {
  label: PropTypes.node.isRequired,
  ariaLabel: PropTypes.string,
  position: PropTypes.func,
  maxWidth: PropTypes.string,
  disabled: PropTypes.bool
};
Tooltip.defaultProps = {
  maxWidth: Math.min(typeof window !== 'undefined' ? window.innerWidth : 500, 500) + 'px'
};
export default Tooltip;